import React, { useState, useEffect } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { msalInstance, loginRequest } from './services/auth';
import * as api from './services/api';
import './styles/app.css';

// ── Page Components ──
// Each module from the PoC is a separate page component
import Dashboard from './pages/Dashboard';
import Requests from './pages/Requests';
import DraftingDesk from './pages/DraftingDesk';
import ReviewAdvice from './pages/ReviewAdvice';
import ITTQualification from './pages/ITTQualification';
import Signatures from './pages/Signatures';
import AgreementLifecycle from './pages/AgreementLifecycle';
import CaseManagement from './pages/CaseManagement';
import ComplianceTracker from './pages/ComplianceTracker';
import BoardMeetings from './pages/BoardMeetings';
import Settings from './pages/Settings';

// ── Navigation Items ──
const NAV_ITEMS = [
  { group: 'Overview', items: [
    { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
  ]},
  { group: 'Service Requests', items: [
    { id: 'requests', label: 'All Requests', icon: '📋', badgeKey: 'totalRequests' },
    { id: 'drafts', label: 'Drafting Desk', icon: '✏️' },
    { id: 'review', label: 'Review & Advice', icon: '🔍', badgeKey: 'pendingReviews', badgeClass: 'red' },
  ]},
  { group: 'Procurement & ITT', items: [
    { id: 'itt', label: 'ITT Qualification', icon: '📦', badgeKey: 'awaitingITT', badgeClass: 'gold' },
  ]},
  { group: 'Legal Assets', items: [
    { id: 'signatures', label: 'Digital Signatures', icon: '✍️' },
    { id: 'lifecycle', label: 'Agreement Lifecycle', icon: '📅', badgeKey: 'expiringAgreements', badgeClass: 'red' },
    { id: 'cases', label: 'Case Management', icon: '⚖️' },
    { id: 'compliance', label: 'Compliance Tracker', icon: '🛡️', badgeKey: 'complianceDue', badgeClass: 'gold' },
  ]},
  { group: 'Admin', items: [
    { id: 'board', label: 'Board & Meetings', icon: '🏛️' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ]},
];

const PAGE_TITLES = {
  dashboard: 'Dashboard Overview',
  requests: 'All Service Requests',
  drafts: 'Drafting Desk',
  review: 'Review & Legal Advice',
  itt: 'ITT Qualification & Classification',
  signatures: 'Digital Signature Repository',
  lifecycle: 'Agreement Lifecycle Management',
  cases: 'Case Management',
  compliance: 'Compliance Tracker',
  board: 'Board & General Meetings',
  settings: 'System Settings',
};

const PAGE_COMPONENTS = {
  dashboard: Dashboard,
  requests: Requests,
  drafts: DraftingDesk,
  review: ReviewAdvice,
  itt: ITTQualification,
  signatures: Signatures,
  lifecycle: AgreementLifecycle,
  cases: CaseManagement,
  compliance: ComplianceTracker,
  board: BoardMeetings,
  settings: Settings,
};

// ── Login Screen ──
function LoginScreen() {
  const { instance } = useMsal();

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-icon"><img src="/desicon-logo.svg" alt="Desicon" style={{width:80,height:80,objectFit:'contain'}} /></div>
        <h1>Desicon Engineering Limited</h1>
        <h2>Legal Service Desk</h2>
        <p>Sign in with your Desicon Microsoft account to continue.</p>
        <button className="login-btn" onClick={() => instance.loginRedirect(loginRequest)}>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}

// ── New Request Modal ──
function NewRequestModal({ isOpen, onClose, onSubmit, legalTeam, currentUser }) {
  const [form, setForm] = useState({
    subject: '', type: 'contract_draft', description: '',
    priority: 'medium', date_required: '', delegated_via_id: '',
  });
  const [attachedFile, setAttachedFile] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit(form, attachedFile);
    setForm({ subject: '', type: 'contract_draft', description: '', priority: 'medium', date_required: '', delegated_via_id: '' });
    setAttachedFile(null);
  };

  const selectedManager = legalTeam.find(u => u.id === form.delegated_via_id);

  return (
    <div className="modal-overlay open" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">New Legal Service Request</div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* FR-077: Auto-populated requester fields from Azure AD */}
        <div style={{display:'flex',gap:14,padding:'10px 14px',background:'var(--pale)',borderRadius:8,marginBottom:16,fontSize:12,alignItems:'center'}}>
          <div style={{width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,var(--mid),var(--navy))',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:13,fontWeight:700,flexShrink:0}}>
            {currentUser?.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase() || '??'}
          </div>
          <div>
            <div style={{fontWeight:600,color:'var(--navy)'}}>{currentUser?.name || 'Loading...'}</div>
            <div style={{color:'var(--muted)',fontSize:11}}>{currentUser?.email || ''} {currentUser?.department ? ` · ${currentUser.department}` : ''}</div>
          </div>
          <div style={{marginLeft:'auto',fontSize:10,color:'var(--muted)',background:'var(--white)',padding:'3px 8px',borderRadius:4,border:'1px solid var(--border)'}}>Auto-populated from Azure AD</div>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label>Request Type</label>
            <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              <option value="contract_draft">Contract Draft</option>
              <option value="nda">NDA Review</option>
              <option value="document_review">Document Review</option>
              <option value="legal_opinion">Legal Opinion</option>
              <option value="employment_contract">Employment Contract</option>
              <option value="itt_qualification">ITT Qualification</option>
              <option value="tenancy_lease">Tenancy/Lease</option>
              <option value="board_resolution">Board Resolution</option>
              <option value="signature_request">Signature Request</option>
              <option value="power_of_attorney">Power of Attorney</option>
              <option value="mou">MOU</option>
              <option value="loi">LOI</option>
            </select>
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="form-group full">
            <label>Subject</label>
            <input type="text" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Brief description of the request" />
          </div>
          <div className="form-group full">
            <label>Detailed Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Provide full details, context, and any specific requirements..." rows={4} />
          </div>
          <div className="form-group">
            <label>Date Required</label>
            <input type="date" value={form.date_required} onChange={e => setForm({...form, date_required: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Route via Line Manager (FR-078)</label>
            <select value={form.delegated_via_id} onChange={e => setForm({...form, delegated_via_id: e.target.value})}>
              <option value="">-- Direct submission to Legal --</option>
              {legalTeam.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g,' ')})</option>)}
            </select>
          </div>
          <div className="form-group full">
            <label>Attach Supporting Document (optional)</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              style={{fontSize:12,padding:7,border:'1px solid var(--border)',borderRadius:8,background:'var(--white)',width:'100%'}}
              onChange={e => setAttachedFile(e.target.files[0] || null)}
            />
            {attachedFile && (
              <div style={{fontSize:11,color:'var(--muted)',marginTop:4,display:'flex',alignItems:'center',gap:6}}>
                📎 {attachedFile.name} ({(attachedFile.size/1024).toFixed(0)} KB)
                <button onClick={()=>setAttachedFile(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--danger)',fontSize:12}}>✕</button>
              </div>
            )}
            <span style={{fontSize:10,color:'var(--muted)',marginTop:2,display:'block'}}>PDF, Word, Excel, or image files up to 50MB</span>
          </div>
        </div>

        {/* FR-078: Delegation chip */}
        {selectedManager && (
          <div style={{display:'flex',alignItems:'center',gap:8,margin:'10px 0',padding:'8px 12px',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,fontSize:12}}>
            <span>📋</span>
            <span style={{color:'#92400E'}}>This request will be routed via <strong>{selectedManager.name}</strong> for approval before reaching the Legal team.</span>
            <button onClick={() => setForm({...form, delegated_via_id: ''})} style={{marginLeft:'auto',cursor:'pointer',background:'none',border:'none',fontSize:14,color:'#92400E'}}>✕</button>
          </div>
        )}

        <div className="form-actions">
          <button className="topbar-btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="topbar-btn btn-primary" onClick={handleSubmit} disabled={!form.subject}>Submit Request</button>
        </div>
      </div>
    </div>
  );
}

// ── Notification Panel ──
function NotificationPanel({ notifications, unreadCount, onClose, onMarkRead, onMarkAllRead }) {
  return (
    <div className="notification-panel">
      <div className="notification-header">
        <span>Notifications ({unreadCount})</span>
        <button onClick={onMarkAllRead} className="sig-btn ghost">Mark all read</button>
        <button onClick={onClose} className="modal-close">&times;</button>
      </div>
      <div className="notification-list">
        {notifications.length === 0 && <p className="notification-empty">No notifications</p>}
        {notifications.map(n => (
          <div key={n.id} className={`notification-item ${n.is_read ? '' : 'unread'}`} onClick={() => onMarkRead(n.id)}>
            <div className="notification-title">{n.title}</div>
            <div className="notification-meta">{new Date(n.created_at).toLocaleString('en-GB')}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Application ──
function AppContent() {
  const { instance, accounts } = useMsal();
  const [activePage, setActivePage] = useState('dashboard');
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [legalTeam, setLegalTeam] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [badges, setBadges] = useState({});
  const [toast, setToast] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const sessionTimeoutRef = React.useRef(null);

  // Session timeout (NFR: 15 minutes inactivity)
  useEffect(() => {
    function resetTimer() {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = setTimeout(() => {
        showToast('Session expired due to inactivity. Please sign in again.');
        instance.logoutRedirect();
      }, 15 * 60 * 1000); // 15 minutes
    }
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetTimer));
      clearTimeout(sessionTimeoutRef.current);
    };
  }, []);

  // Load initial data
  useEffect(() => {
    loadUser();
    loadNotifications();
    loadBadges();

    // Auto-refresh notifications every 2 minutes
    const notifInterval = setInterval(() => {
      loadNotifications();
      loadBadges();
    }, 2 * 60 * 1000);

    return () => clearInterval(notifInterval);
  }, []);

  async function loadUser() {
    try {
      const { data } = await api.getCurrentUser();
      setCurrentUser(data);
      const { data: team } = await api.getLegalTeam();
      setLegalTeam(team);
    } catch (err) {
      console.error('Failed to load user:', err);
    }
  }

  async function loadNotifications() {
    try {
      const { data } = await api.getNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  }

  async function loadBadges() {
    try {
      const { data } = await api.getDashboardAlerts();
      setBadges({
        totalRequests: data.overdueRequests + data.pendingReviews,
        pendingReviews: data.pendingReviews,
        expiringAgreements: data.expiringAgreements,
      });
    } catch (err) {
      console.error('Failed to load badges:', err);
    }
  }

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(null), 4000);
  }

  async function handleNewRequest(formData, attachedFile) {
    try {
      const { data } = await api.createRequest(formData);
      // POST /requests returns the request row directly. Tolerate a wrapped
      // shape too, in case the endpoint ever changes.
      const newRequest = data?.request || data;
      const requestId  = newRequest?.id;
      const requestRef = newRequest?.ref || newRequest?.ref_number || requestId;

      if (attachedFile) {
        if (!requestId) {
          // Never fail silently — the requester must know the file did not attach.
          setShowNewRequest(false);
          loadBadges();
          showToast('⚠️ Request submitted, but the attachment could NOT be linked. Please upload it from the request page.');
          return;
        }
        try {
          const fd = new FormData();
          fd.append('file', attachedFile);
          fd.append('linked_entity_type', 'request');
          fd.append('linked_entity_id', requestId);
          fd.append('category', 'request_attachment');
          fd.append('folder_path', `Legal/Requests/${requestRef}`);
          await api.uploadDocument(fd);
        } catch (uploadErr) {
          setShowNewRequest(false);
          loadBadges();
          showToast('⚠️ Request submitted but the document upload FAILED: '
            + (uploadErr.response?.data?.error || uploadErr.message)
            + ' — please attach it manually.');
          return;
        }
      }

      setShowNewRequest(false);
      showToast(attachedFile
        ? `✅ Request ${requestRef} submitted with attachment`
        : `✅ Request ${requestRef} submitted`);
      loadBadges();
    } catch (err) {
      showToast('Failed to submit request: ' + (err.response?.data?.error || err.message));
    }
  }

  async function handleSearch(query) {
    setSearchQuery(query);
    if (query.length >= 2) {
      try {
        const { data } = await api.globalSearch(query);
        setSearchResults(data);
      } catch (err) {
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  }

  async function handleMarkRead(id) {
    await api.markRead(id);
    loadNotifications();
  }

  async function handleMarkAllRead() {
    await api.markAllRead();
    loadNotifications();
  }

  const PageComponent = PAGE_COMPONENTS[activePage];
  const userInitials = currentUser ? currentUser.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '??';

  return (
    <div className="app-container">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-logo">
            <img src="/desicon-logo.svg" alt="Desicon" className="brand-icon-img" />
            <div>
              <div className="brand-name">Desicon Engineering</div>
              <div className="brand-sub">Legal Service Desk</div>
            </div>
          </div>
        </div>

        <nav>
          {NAV_ITEMS.map(group => (
            <div className="nav-group" key={group.group}>
              <div className="nav-label">{group.group}</div>
              {group.items.map(item => (
                <div
                  key={item.id}
                  className={`nav-item ${activePage === item.id ? 'active' : ''}`}
                  onClick={() => setActivePage(item.id)}
                >
                  <span className="icon">{item.icon}</span>
                  {item.label}
                  {item.badgeKey && badges[item.badgeKey] > 0 && (
                    <span className={`nav-badge ${item.badgeClass || ''}`}>
                      {badges[item.badgeKey]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          v1.0.0 · Legal Ops Platform<br/>
          &copy; {new Date().getFullYear()} Desicon Engineering Ltd.
        </div>
      </aside>

      {/* Main Content */}
      <div className="main">
        {/* Topbar */}
        <header className="topbar">
          <div className="topbar-title">{PAGE_TITLES[activePage]}</div>
          <div className="topbar-search">
            <span>🔍</span>
            <input
              type="text"
              placeholder="Search requests, agreements, cases..."
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
            />
            {searchResults.length > 0 && (
              <div className="search-dropdown">
                {searchResults.map(r => (
                  <div key={r.id} className="search-result" onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    // Navigate to the relevant page
                    const pageMap = { request: 'requests', agreement: 'lifecycle', case: 'cases', itt: 'itt' };
                    setActivePage(pageMap[r.entity_type] || 'dashboard');
                  }}>
                    <span className="search-ref">{r.ref}</span>
                    <span className="search-title">{r.title}</span>
                    <span className="search-type">{r.entity_type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="topbar-btn btn-primary" onClick={() => setShowNewRequest(true)}>
            + New Request
          </button>
          <button className="topbar-btn btn-ghost" style={{ position: 'relative' }} onClick={() => setShowNotifications(!showNotifications)}>
            🔔
            {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
          </button>
          <button
            className={`topbar-btn ${editMode ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setEditMode(!editMode); showToast(editMode ? 'Edit Mode OFF' : 'Edit Mode ON: click any table cell to edit'); }}
            title="Toggle Edit Mode (FR-079)"
          >
            ✏️ {editMode ? 'Exit Edit' : 'Edit Mode'}
          </button>
          <div className="avatar" title={currentUser ? `${currentUser.name} — ${currentUser.role}` : ''}>
            {userInitials}
          </div>
        </header>

        {/* Notifications Panel */}
        {showNotifications && (
          <NotificationPanel
            notifications={notifications}
            unreadCount={unreadCount}
            onClose={() => setShowNotifications(false)}
            onMarkRead={handleMarkRead}
            onMarkAllRead={handleMarkAllRead}
          />
        )}

        {/* Page Content */}
        <div className="content">
          {PageComponent && <PageComponent showToast={showToast} currentUser={currentUser} editMode={editMode} />}
        </div>
      </div>

      {/* New Request Modal */}
      <NewRequestModal
        isOpen={showNewRequest}
        onClose={() => setShowNewRequest(false)}
        onSubmit={handleNewRequest}
        legalTeam={legalTeam}
        currentUser={currentUser}
      />

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

// ── Root App with MSAL Provider ──
export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <AppContent />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}
